<template>
  <div class="task-dialog" :class="{ 'task-dialog--fullscreen': isFullScreen }">
    <div class="task-dialog__header">
      <div class="task-dialog__header-left">
        <BaseRoundButton
          :size="isPhone ? 44 : 36"
          :icon="isPhone ? 'arrow-left' : 'chevrons-right'"
          is-transparent
          :aria-label="$t('actions.close')"
          @click.native="$emit('close')"
        />
        <BaseRoundButton
          v-if="!isPhone"
          :size="36"
          :icon="isFullScreen ? 'minimize-2' : 'maximize-2'"
          is-transparent
          @click.native="toggleFullScreen"
        />
      </div>
      <div class="task-dialog__header-actions">
        <BaseButton
          v-if="mode === 'view' && !isPhone"
          is-outlined
          is-small
          has-icon-right
          is-square
          :disabled="isFromTemplate"
          :tooltip-text="isFromTemplate ? $t('opportunity-tasks.task-details.same-as-template') : ''"
          @click.native="onAddToTemplate"
        >
          {{
            isFromTemplate
              ? $t('opportunity-tasks.task-details.added-to-template')
              : $t('opportunity-tasks.task-details.add-to-template')
          }}
          <BaseIcon :name="isFromTemplate ? 'check' : 'plus'" />
        </BaseButton>
        <BaseButton
          v-if="mode === 'view' && !isPhone"
          is-outlined
          is-small
          is-square
          has-icon-right
          @click.native="onDelete"
        >
          {{ $t('actions.delete') }}
          <BaseIcon name="trash-2" />
        </BaseButton>
        <BaseButton v-if="mode === 'edit'" is-transparent is-small @click.native="onCancel">
          {{ $t('actions.cancel') }}
        </BaseButton>
        <BaseButton
          :is-outlined="mode === 'view'"
          is-small
          has-icon-right
          is-square
          :disabled="isSubmitDisabled"
          :tooltip-text="isFromTemplate ? $t('opportunity-tasks.task-details.cannot-edit') : ''"
          @click.native="onSubmit"
        >
          {{
            mode === 'view'
              ? $t('actions.edit')
              : mode === 'edit'
              ? $t('opportunity-tasks.task-details.edit')
              : $t('opportunity-tasks.task-details.create')
          }}
          <BaseIcon v-if="mode === 'view'" name="edit-2" />
        </BaseButton>
        <!-- Phones: the other actions behind a ⋯, the header keeping one line. -->
        <BaseMoreActions
          v-if="mode === 'view' && isPhone"
          :title="task.title"
          :actions="moreActions"
          @select="$event === 'add-to-template' ? onAddToTemplate() : onDelete()"
        />
      </div>
    </div>

    <DeleteTaskDialog ref="deleteTaskDialog" @confirm="confirmDelete" />

    <div class="task-dialog__body">
      <div class="task-dialog__body-left">
        <div class="task-dialog__section">
          <div v-if="mode === 'view'" class="task-dialog__title-row">
            <h1 class="task-dialog__title">
              {{ task.title }}
            </h1>
            <BaseIcon
              v-if="isFromTemplate"
              name="lock"
              class="tasks-card__lock-icon"
              v-tooltip="{ content: $t('opportunity-tasks.task-details.cannot-edit') }"
            />
          </div>
          <FormInput
            v-else
            ref="inputTitle"
            hasGrayBackground
            v-model="form.title"
            name="task-title"
            class="task-dialog__title-input"
            :placeholder="$t('opportunity-tasks.task-details.title')"
          />
        </div>

        <section class="task-dialog__section">
          <h2 class="task-dialog__section-title">{{ $t('opportunity-tasks.task-details.description') }}</h2>
          <template v-if="mode === 'view'">
            <span v-if="!task.description" class="task-dialog__description-empty">
              {{ $t('basic-words.none') }}
            </span>
            <span v-else class="task-dialog__description">
              {{ task.description }}
            </span>
          </template>
          <FormInput
            v-else
            hasGrayBackground
            v-model="form.description"
            name="task-description"
            is-textarea
            class="task-dialog__description-textarea"
            :placeholder="$t('opportunity-tasks.task-details.description')"
          />
        </section>

        <section class="task-dialog__section">
          <h2 class="task-dialog__section-title">{{ $t('opportunity-tasks.task-details.extraits') }}</h2>
          <div v-if="task.citations.length === 0" class="task-dialog__citation-empty">
            {{ $t('opportunity-tasks.task-details.no-extraction') }}
          </div>
          <div v-for="(citation, idx) in task.citations" :key="`citation-${idx}`" class="task-dialog__citation">
            <BaseExtraction
              :text="citation.value"
              :method="citation.source"
              :source="citation.opportunityDocumentId"
              :evalGridValue="null"
              :isCompact="true"
              @extraction-source-clicked="() => goToCitationSource(citation)"
              @delete="() => deleteCitation(citation)"
              actionable
            />
          </div>
        </section>
      </div>

      <div class="task-dialog__meta">
        <div class="task-dialog__meta-row">
          <span class="task-dialog__meta-label">{{ $t('opportunity-tasks.task-details.status') }}</span>
          <div class="task-dialog__status">
            <BaseStatusMenu
              ref="taskStatusMenu"
              :items="taskStatuses"
              :current-status="currentStatus"
              attach=".task-dialog__status"
              @change="onStatusChange"
              searchAllowed
            />
          </div>
        </div>
        <div class="task-dialog__meta-row">
          <span class="task-dialog__meta-label">{{ $t('opportunity-tasks.task-details.assigned-to') }}</span>
          <div :class="`dialog-assignees-${task.id}`">
            <BaseAssignation
              :value="assignees"
              @change-users="onAssign"
              :attach="`.dialog-assignees-${task.id}`"
              tooltipPlacement="bottom"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon';
import DeleteTaskDialog from '@/components/page-opportunity-tasks/DeleteTaskDialog.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseAssignation from '@/components/base/BaseAssignation.vue';
import BaseStatusMenu from '@/components/base/BaseStatusMenu.vue';
import { mapActions, mapGetters } from 'vuex';
import { closeOnBack } from '@/util/back-button';
import { viewport } from '@/util/viewport';
import FormInput from '@/components/form/FormInput.vue';
import BaseExtraction from '@/components/base/BaseExtraction.vue';

export default {
  name: 'TaskDialog',
  components: {
    BaseExtraction,
    DeleteTaskDialog,
    BaseStatusMenu,
    BaseAssignation,
    BaseRoundButton,
    BaseMoreActions,
    BaseButton,
    BaseIcon,
    FormInput,
  },
  props: {
    task: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isFullScreen: false,
      mode: this.task && this.task.id ? 'view' : 'create', // 'view' | 'edit' | 'create'
      form: {
        title: '',
        description: '',
        status: null,
        assignations: [],
      },
    };
  },
  computed: {
    ...mapGetters('taskStatuses', ['taskStatuses']),
    ...mapGetters('taskTemplates', ['taskTemplates']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('annotations', ['annotations']),
    currentStatus() {
      return this.mode === 'view' ? this.task.taskStatus : this.form.status;
    },
    assignees() {
      return this.mode === 'view'
        ? this.task.assignations.map((a) => ({ ...a, userId: a.id ? a.id : a.userId }))
        : this.form.assignations.map((a) => ({ ...a, userId: a.id ? a.id : a.userId }));
    },
    isFromTemplate() {
      return !!this.task.taskTemplateId;
    },
    isSubmitDisabled() {
      return !this.form.title || this.isFromTemplate;
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    moreActions() {
      return [
        ...(this.isFromTemplate
          ? []
          : [
              {
                key: 'add-to-template',
                label: this.$t('opportunity-tasks.task-details.add-to-template'),
                icon: 'plus',
              },
            ]),
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
  },
  // On phones, back closes it, as any panel over the page (util/back-button).
  mounted() {
    this.releaseBack = closeOnBack(() => this.$emit('close'));
  },
  beforeDestroy() {
    this.releaseBack();
  },
  watch: {
    task: {
      handler(newTask, oldTask) {
        const taskHasChanged = (!!newTask && !oldTask) || (!newTask && !!oldTask) || newTask.id !== oldTask.id;
        if (!newTask || !newTask.id) {
          this.mode = 'create';
        } else if (taskHasChanged) {
          this.mode = 'view';
        }
        this.resetForm();
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('tasks', [
      'createOpportunityTask',
      'updateOpportunityTask',
      'deleteOpportunityTask',
      'updateOpportunityTaskAssignees',
      'saveOpportunityTaskAsTemplate',
    ]),
    ...mapActions('taskCitations', ['createTaskCitation', 'deleteTaskCitation']),
    ...mapActions('taskTemplates', ['createTaskTemplate']),
    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
      this.$emit('fullscreen', this.isFullScreen);
    },
    onAssign(users) {
      // if not in view mode, then store the assignees until time of submission
      if (this.mode !== 'view') {
        this.form.assignations = users;
      }
      // in other modes, update the task's assignees
      else {
        this.updateOpportunityTaskAssignees({
          opportunityId: this.task.opportunityId,
          taskId: this.task.id,
          assigneeIds: users.map((u) => u.userId),
        }).then((updatedTask) => {
          this.$emit('submit', updatedTask);
        });
      }
    },
    onStatusChange(newStatus) {
      if (!newStatus) {
        return;
      }
      // if not in view mode, then store the status until time of submission
      if (this.mode !== 'view') {
        this.form.status = newStatus;
      }
      // in other modes, update the task's status
      else {
        const payload = {
          id: this.task.id,
          opportunityId: this.task.opportunityId,
          taskStatusId: newStatus.id,
          title: this.task.title,
          description: this.task.description,
        };
        this.updateOpportunityTask({ task: payload }).then((updatedTask) => {
          this.$emit('submit', updatedTask);
        });
      }
    },
    onAddToTemplate() {
      this.saveOpportunityTaskAsTemplate({ opportunityId: this.task.opportunityId, taskId: this.task.id }).then(
        (savedTask) => {
          this.$emit('submit', savedTask);
        },
      );
    },
    onDelete() {
      this.$refs.deleteTaskDialog.openDialog(this.task);
    },
    confirmDelete() {
      this.deleteOpportunityTask({
        opportunityId: this.task.opportunityId,
        taskId: this.task.id,
      }).then(() => {
        this.$emit('close');
      });
    },
    onSubmit() {
      if (this.mode === 'create') {
        this.handleCreate();
      }
      // update existing task
      else if (this.mode === 'edit') {
        this.handleUpdate();
      } else {
        this.enterEditMode();
      }
    },
    handleCreate() {
      const statusOpen = (this.taskStatuses || []).find((s) => s.statusType === 'open');
      const statusOpenId = statusOpen ? statusOpen.id : null;
      const payload = {
        opportunityId: this.task.opportunityId,
        title: this.form.title,
        description: this.form.description,
        taskStatusId: this.form.status
          ? this.form.status.id
          : this.task.taskStatus
          ? this.task.taskStatus.id
          : statusOpenId,
      };
      this.createOpportunityTask({ task: payload }).then((createdTask) => {
        const assigneePromise =
          this.assignees.length > 0
            ? this.updateOpportunityTaskAssignees({
                opportunityId: createdTask.opportunityId,
                taskId: createdTask.id,
                assigneeIds: this.assignees.map((u) => u.userId),
              })
            : Promise.resolve(createdTask);

        const citationPromises =
          this.task.citations && this.task.citations.length > 0
            ? Promise.all(
                this.task.citations.map((citation) =>
                  this.createTaskCitation({
                    opportunityId: createdTask.opportunityId,
                    taskId: createdTask.id,
                    citation,
                  }),
                ),
              )
            : Promise.resolve([]);

        Promise.all([assigneePromise, citationPromises]).then(([taskResult, citations]) => {
          this.$emit('submit', { ...taskResult, citations });
        });
      });
    },
    handleUpdate() {
      const payload = {
        id: this.task.id,
        opportunityId: this.task.opportunityId,
        taskStatusId: this.form.status.id,
        title: this.form.title,
        description: this.form.description,
      };
      this.updateOpportunityTask({ task: payload }).then((updatedTask) => {
        this.updateOpportunityTaskAssignees({
          opportunityId: this.task.opportunityId,
          taskId: this.task.id,
          assigneeIds: this.assignees.map((u) => u.userId),
        }).then((updatedTask) => {
          this.$emit('submit', updatedTask);
          this.mode = 'view';
        });
      });
    },
    enterEditMode() {
      this.resetForm();
      this.mode = 'edit';
      this.focusOnTitleInput();
    },
    onCancel() {
      this.resetForm();
      this.mode = 'view';
    },
    resetForm() {
      this.form.title = this.task.title || '';
      this.form.description = this.task.description || '';
      this.form.status = this.task.taskStatus || null;
      this.form.assignations = this.task.assignations || [];
    },
    isCitationExternal(citation) {
      return ['PDF_ANNOTATION', 'LLM_EXTRACTION'].includes(citation.source);
    },
    getCitationSourcePage(citation) {
      if (citation.source === 'PDF_ANNOTATION') {
        const annotation = this.annotations.find((annotation) => annotation.id === citation.textAnnotationId);
        if (!annotation) {
          return 0;
        }
        return annotation.startPage;
      } else {
        return citation.opportunityDocumentPage ? citation.opportunityDocumentPage : 0;
      }
    },
    goToCitationSource(citation) {
      if (!this.isCitationExternal(citation)) {
        return;
      }

      const page = this.getCitationSourcePage(citation);
      if (page === 0) {
        return;
      }
      this.$router
        .push({
          name: 'opportunity-analysis',
          params: { opportunityId: this.opportunity.id, documentId: citation.opportunityDocumentId },
          query: { page: !page ? 1 : page },
        })
        .catch((e) => {
          // same document and page already in the URL. Emit so the parent can scroll.
          if (e.name === 'NavigationDuplicated') {
            this.$emit('scroll-to-page', !page ? 1 : page);
          }
        });
    },
    deleteCitation(citation) {
      this.deleteTaskCitation({
        opportunityId: this.task.opportunityId,
        taskId: this.task.id,
        citationId: citation.id,
      }).then(() => {
        this.$emit('delete-citation', citation);
      });
    },
    focusOnTitleInput() {
      this.$nextTick(() => {
        if (this.$refs.inputTitle && this.$refs.inputTitle.$el) {
          const input = this.$refs.inputTitle.$el.querySelector('input');
          if (input) input.focus();
        }
      });
    },
  },
};
</script>

<style lang="scss">
.task-dialog {
  background-color: $color-white;
  box-shadow: $shadow-default;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 100%;

  &--fullscreen {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: $top-index;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid $color-border;
    flex-shrink: 0;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @include screen-below('tablet') {
    &__header {
      padding: 6px 12px 6px 6px;
    }
  }

  &__body {
    padding: 0 16px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;

    display: flex;
    flex-direction: row;

    &-left {
      flex: 1;
      padding-top: 16px;
      padding-right: 16px;
    }

    @media screen and (max-width: 1175px) {
      flex-direction: column;
      flex: unset;
    }
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__title {
    margin: 0;
    font-size: $font-size-xbigger;
    line-height: 1.25;
    font-weight: 800;
    color: $color-normal;
  }

  &__description {
    font-size: $font-size-small;
    line-height: 1.6;
    color: $color-normal;

    &-empty {
      color: $color-neutral;
      font-size: $font-size-small;
    }
  }

  &__title-input {
    padding: 0 !important;

    .form-input__input {
      font-size: $font-size-xbigger !important;
      font-weight: 600;
    }
  }

  &__description-textarea {
    padding: 0 !important;
  }

  &__creator {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 24px;
  }

  &__creator-avatar {
    flex-shrink: 0;
  }

  &__creator-text {
    font-size: $font-size-small;
    color: $color-normal-pale;
  }

  &__section {
    margin-bottom: 24px;
  }

  &__section-title {
    margin: 0 0 6px;
    font-size: $font-size-small;
    font-weight: 700;
    color: $color-normal;
  }

  &__citation {
    &-empty {
      font-size: $font-size-small;
      color: $color-neutral;
    }
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0 16px 16px;
    border-left: 1px solid $color-border;
    width: 35%;
    min-width: 200px;
    max-width: 400px;

    @media screen and (max-width: 1175px) {
      border-left: 0;
      border-top: 1px solid $color-border;
      padding: 16px 0;
      width: 100%;
      max-width: 100%;
    }
  }

  &__meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: 8px 8px 16px 0;
    border-bottom: 1px solid $color-border;
  }

  &__meta-label {
    flex-shrink: 0;
    font-size: $font-size-small;
    font-weight: 600;
    color: $color-normal-pale;
    min-width: 80px;
  }
}
</style>
